import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import { Alert, Card, Col, Row, Segmented, Space, Statistic } from 'antd';
import { Line } from '@ant-design/charts';
import { BOTTOM_LEGEND } from '@/pages/dashboard/chartLegend';
import type { SystemHealthStatsOut } from '@/api/generated/schemas';

const WINDOWS = [
  { label: '24h', hours: 24, days: 7 },
  { label: '48h', hours: 48, days: 14 },
  { label: '7 dni', hours: 168, days: 30 },
];

export const SettlementSystemHealthCard = () => {
  const [windowIdx, setWindowIdx] = useState(0);
  const { hours, days } = WINDOWS[windowIdx]!;

  const { query } = useCustom<SystemHealthStatsOut>({
    url: 'settlement/charges_monthly/stats/system_health',
    method: 'get',
    config: { query: { hours, days } },
    queryOptions: { refetchInterval: 60_000 },
  });

  const stats = query.data?.data;
  const anomalyCount = stats?.anomaly_count ?? 0;
  const silentCount = stats?.silent_publisher_ids?.length ?? 0;

  return (
    <Card
      title="Zdrowie systemu"
      extra={
        <Segmented
          options={WINDOWS.map((w, i) => ({ label: w.label, value: i }))}
          value={windowIdx}
          onChange={(v) => setWindowIdx(v as number)}
        />
      }
      loading={query.isLoading}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {silentCount > 0 && (
          <Alert
            type="warning"
            message={`${silentCount} wydawców bez naliczeń przez ostatnie ${hours}h`}
            showIcon
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={12} sm={8}>
            <Statistic
              title="Anomalie (naliczenie > długość produktu)"
              value={anomalyCount}
              valueStyle={{ color: anomalyCount > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Wydawcy z anomaliami"
              value={stats?.affected_publishers ?? 0}
              valueStyle={{
                color:
                  (stats?.affected_publishers ?? 0) > 0 ? '#d46b08' : undefined,
              }}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Produkty z anomaliami"
              value={stats?.affected_products ?? 0}
              valueStyle={{
                color:
                  (stats?.affected_products ?? 0) > 0 ? '#d46b08' : undefined,
              }}
            />
          </Col>
        </Row>

        <Line
          data={stats?.ingestion_rate ?? []}
          xField="hour"
          yField="charge_count"
          height={180}
          legend={BOTTOM_LEGEND}
          axis={{
            x: { labelFormatter: (v: string) => v.slice(11, 16) },
          }}
        />
      </Space>
    </Card>
  );
};
